import type {
  TProduct,
  AlgoliaProductType,
  ProductTileType,
  ProductTileAssets,
  ProductCardHorizontal,
  VisenzeProductTypeWithClPrices,
} from './../components/WrapperProductTile.props'
import { convertStringToSlug } from '@design-system/utils/stringUtils'
import type { Brand } from '@design-system/index'

/**
 * Composable for normalizing and transforming product data.
 *
 * @description
 * Provides methods to standardize product information from different sources
 * (Algolia, Visenze) into a consistent product tile format.
 *
 * @returns {Object} An object with product normalization and transformation methods.
 */
export const useWrapperProductTile = () => {
  const { currentWebsite: _current, isGiorgioArmaniWebsite: _isGa } =
    useWebsite()
  const { pdpPath, homeBrandPath } = useRouteHelper()
  const currentWebsite = _current.value
  const isGiorgioArmaniWebsite = _isGa.value

  /**
   * Normalizes product images from various sources.
   *
   * @param {Array<{URL?: string, ImageURL?: string, priority?: number, type?: string}>} assets - Product image assets.
   * @returns {ProductTileAssets[]} Normalized product images.
   * @description
   * - Sorts images by priority
   * - Handles different image URL formats
   * - Determines image type (image or video)
   */
  const normalizeImages = (
    assets:
      | Array<{
          URL?: string
          ImageURL?: string
          priority?: number
          type?: string
        }>
      | undefined
  ): ProductTileAssets[] => {
    if (!assets || !Array.isArray(assets) || assets.length === 0) {
      return []
    }
    // order assets by priority
    const assetsBypriority = assets.sort(
      ({ priority: a }, { priority: b }) => a! - b!
    )

    return assetsBypriority.map(media => ({
      URL: media.URL ?? media.ImageURL ?? '',
      priority: media.priority,
      type: media.type === 'mov' ? 'TResponsiveVideo' : 'TResponsiveImage',
    }))
  }

  /**
   * Normalizes a product from different sources into a standard format.
   *
   * @param {TProduct} product - The product to normalize.
   * @returns {ProductTileType | undefined} The normalized product.
   * @description
   * - Supports normalization of Algolia and Visenze product types
   * - Extracts and standardizes product information
   * - Handles different product data structures
   */
  const normalizeProduct = (product: TProduct): ProductTileType | undefined => {
    // Algolia product normalization
    if (typeof (product as AlgoliaProductType).objectID !== 'undefined') {
      const item = product as AlgoliaProductType & { product_url?: string }
      const totalAvailableQty = item.size.reduce(
        (acc, { avEcom, avRet }) => acc + ((avEcom ?? 0) + (avRet ?? 0)),
        0
      )

      return {
        sku: item.objectID,
        name: item.Name,
        brand: item.Brand,
        linkToProduct: item.product_url
          ? toRelativeUrl(item.product_url)
          : pdpPath(item, true, false),
        categories: extractCategoriesUnlocalized(
          item?.hierarchicalCategories || [],
          item?.categoryPageId2 || []
        ),
        isAvailable3d: !!item.assets?.find(media => media.type === 'glb'),
        lastInStock: totalAvailableQty === 1,
        images: normalizeImages(item.images || item.assets),
        labels: item.tags,
        colors: item['alternative-color']
          ? item['alternative-color'].length + 1
          : 1,
        inventory: {
          isAvailableOnline: item?.size.length
            ? item?.size?.some(
                // @ts-ignore
                item => item.avEcom && item.avEcom > 0
              )
            : false,
          isAvailableInStore: item?.size.length
            ? item?.size?.some(
                // @ts-ignore
                item => item.avRet && item.avRet > 0
              )
            : false,
        },
        defaultColor: item.ColorDesc ?? '',
        // if the price has already been provided by CL use it, otherwise create the fallback price using the algolia FullPrice
        price: item.clPrices ?? {
          originalPrice: item.FullPrice,
        },
        defaultSku: item?.size?.[0]?.SKU ?? '',
        MFC: item.MFC,
        infoCommerce: item.infoCommerce ?? false,
        showLabels: !isGiorgioArmaniWebsite,
        NameURL: item.NameURL,
        objectID: item.objectID,
        Brand: item.Brand,
      }
    }
    // Visenze product normalization
    else if (
      typeof (product as VisenzeProductTypeWithClPrices).product_id !==
      'undefined'
    ) {
      const item = product as VisenzeProductTypeWithClPrices

      const {
        title,
        brand,
        tags,
        alternative_color,
        product_url,
        size_list,
        color,
        price,
        additional_image_url,
      } = item?.data ?? {}

      const sizes = (size_list ?? []).map(item => stringToObject(item))
      const images = [item.main_image_url, ...(additional_image_url ?? [])]

      const assets = images.map((media, index) => {
        return {
          URL: media,
          priority: index + 1,
          type: getFileTypeFromUrl(media),
        }
      })

      if (!product_url) {
        console.error(
          `Visenze (prd. ${item.product_id}): the 'product_url' is not valid`
        )
      }

      return {
        sku: item.product_id,
        name: title ?? '',
        brand: brand ?? '',
        linkToProduct: product_url
          ? toRelativeUrl(product_url)
          : homeBrandPath.value,
        isAvailable3d: !!assets.find(media => media.type === 'glb'),
        labels: tags ?? [],
        colors: alternative_color ? alternative_color.length + 1 : 1,
        inventory: {
          isAvailableOnline: sizes.some(
            // @ts-ignore
            item => item.avEcom && item.avEcom > 0
          ),
          isAvailableInStore: sizes.some(
            // @ts-ignore
            item => item.avRet && item.avRet > 0
          ),
        },
        images: normalizeImages(assets),
        price: item.clPrices ?? {
          originalPrice: Number(price.value),
        },
        defaultColor: color ?? '',
        defaultSku: sizes.at(0)?.SKU ?? '',
        showLabels: !isGiorgioArmaniWebsite,
      }
    }
  }

  /**
   * Transforms a product into a horizontal product card format.
   *
   * @param {ProductTileType} product - The product to be transformed.
   * @returns {ProductCardHorizontal} The transformed product card.
   * @description
   * - Converts a product tile to a horizontal card format
   * - Extracts key information for display
   * - Handles pricing, availability, and metadata
   */
  const transformToProductCardHorizontal = (
    product: ProductTileType
  ): ProductCardHorizontal => {
    return {
      id: product.sku,
      image: product.images[0]?.URL,
      info: {
        name: product.name,
        oldPrice: product.price.discountedPrice
          ? product.price.originalPrice
          : undefined,
        price: product.price.discountedPrice ?? product.price.originalPrice,
        percentageDiscount: product.price.discountLabel,
        color: product.defaultColor,
      },
      metadata: product.brand
        ? (convertStringToSlug(product.brand) as Brand)
        : currentWebsite,
      path: product.linkToProduct,
      skuCode: product.defaultSku ?? '',
      available:
        product.inventory.isAvailableInStore ||
        product.inventory.isAvailableOnline,
      quantity: 1,
      itemTotalPrice: product.price.originalPrice,
    }
  }

  return {
    /** Normalizes products from different sources */
    normalizeProduct,
    /** Transforms products to horizontal card format */
    transformToProductCardHorizontal,
  }
}
